import { Link } from 'react-router-dom';
import SectionWrapper from '@/components/feature/SectionWrapper';
import { programs } from '@/mocks/programs';

const labels = ['Most Popular', 'Best for Beginners', 'Elite Training', 'Home Comfort'];

const bulletMap: Record<string, string[]> = {
  'private-training': [
    'Personalized coaching plan',
    'Strength & conditioning',
    'Real fight techniques',
  ],
  'group-training': [
    'High-energy team sessions',
    'Partner drills & sparring',
    'All fitness levels welcome',
  ],
  'youth-program': [
    'Age-appropriate curriculum',
    'Discipline & confidence building',
    'Safe supervised environment',
  ],
  'home-training': [
    'Coach visits your location',
    'All equipment provided',
    'Flexible time slots',
  ],
};

export default function ProgramsPreview() {
  return (
    <section className="bg-boxing-black py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <SectionWrapper className="text-center mb-10 md:mb-14">
          <span className="section-label">/ Our Programs</span>
          <h2 className="heading-mixed text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
            Training <span className="text-highlight">Programs</span>
          </h2>
          <div className="w-16 h-[2px] bg-boxing-red mx-auto mt-5" />
          <p className="text-boxing-textgray text-sm max-w-md mx-auto mt-4">
            Professional training programs designed for all levels
          </p>
        </SectionWrapper>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {programs.map((program, index) => {
            const bullets = bulletMap[program.id] || [];
            return (
              <SectionWrapper key={program.id} delay={index * 120}>
                <div className="group relative rounded-xl overflow-hidden border border-white/5 h-full min-h-[420px] md:min-h-[480px] flex flex-col justify-end transition-all duration-300 hover:border-boxing-red/40 hover:shadow-[0_12px_32px_rgba(0,0,0,0.5),0_0_20px_rgba(255,30,30,0.12)]">
                  {/* Background image */}
                  <img
                    src={program.image}
                    alt={program.name}
                    className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                  />

                  {/* Dark gradient overlay — stronger at bottom for text readability */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/30" />
                  {/* Inner shadow for depth */}
                  <div className="absolute inset-0 shadow-[inset_0_-60px_60px_rgba(0,0,0,0.6)] pointer-events-none" />

                  {/* Red accent on hover */}
                  <div className="absolute inset-0 bg-boxing-red/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  {/* Content */}
                  <div className="relative z-10 p-5 md:p-6">
                    {/* Label badge */}
                    <span className="tag-red mb-3 inline-flex">{labels[index]}</span>

                    {/* Title */}
                    <h3 className="font-oswald uppercase text-lg md:text-xl tracking-wide text-white mb-2">
                      {program.name}
                    </h3>

                    {/* Short description */}
                    <p className="text-white/70 text-sm leading-relaxed mb-3">
                      {program.shortDescription}
                    </p>

                    {/* Bullet points */}
                    <ul className="flex flex-col gap-1.5 mb-5">
                      {bullets.map((b) => (
                        <li key={b} className="flex items-center gap-2">
                          <span className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
                            <i className="ri-check-line text-boxing-red text-xs" />
                          </span>
                          <span className="text-white/60 text-xs">{b}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to={`/programs/${program.id}`}
                      className="btn-primary text-xs w-full"
                    >
                      View Details
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-sm" />
                      </span>
                    </Link>
                  </div>
                </div>
              </SectionWrapper>
            );
          })}
        </div>

        <SectionWrapper className="text-center mt-8 md:mt-12">
          <Link
            to="/programs"
            className="btn-secondary text-sm"
          >
            View All Programs
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm" />
            </span>
          </Link>
        </SectionWrapper>
      </div>
    </section>
  );
}
